/**
 * 后台状态检查服务
 * 检查后台项目是否启动
 */

import { API_CONFIG } from './config';

export interface BackendStatus {
  isOnline: boolean;
  message: string;
  timestamp: number;
}

class BackendStatusService {
  private checkTimeout = 3000; // 3秒超时，减少等待时间
  private isChecking = false;
  private cachedStatus: BackendStatus | null = null;
  private cacheExpiry = 60000; // 60秒缓存，成功后缓存更长时间
  private checkPromise: Promise<BackendStatus> | null = null; // 防止重复请求

  /**
   * 异步检查后台服务状态（优化版本：只尝试一个端点，成功后停止）
   */
  public async checkBackendStatus(): Promise<BackendStatus> {
    const now = Date.now();
    
    // 检查缓存是否有效
    if (this.cachedStatus && (now - this.cachedStatus.timestamp) < this.cacheExpiry) {
      return this.cachedStatus;
    }

    // 如果正在检查，返回正在进行的Promise
    if (this.checkPromise) {
      return this.checkPromise;
    }

    // 防抖处理
    if (this.isChecking) {
      return {
        isOnline: false,
        message: '正在检查中，请稍候...',
        timestamp: now,
      };
    }

    this.isChecking = true;
    
    // 创建检查Promise
    this.checkPromise = this.performHealthCheck();
    
    try {
      const result = await this.checkPromise;
      return result;
    } finally {
      this.isChecking = false;
      this.checkPromise = null;
    }
  }

  /**
   * 执行实际的健康检查
   */
  private async performHealthCheck(): Promise<BackendStatus> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.checkTimeout);

      // 只使用一个最有效的端点，避免多次请求
      const healthEndpoint = `${API_CONFIG.BASE_URL}/public/health`;
      
      
      const response = await fetch(healthEndpoint, {
        method: 'GET',
        signal: controller.signal,
        headers: {
          'Accept': 'application/json, text/plain, */*',
        },
        mode: 'cors',
        credentials: 'include'
      });

      clearTimeout(timeoutId);

      // 任何2xx或3xx响应都认为服务在线
      if (response.ok || response.status === 304) {
        const status: BackendStatus = {
          isOnline: true,
          message: '后台服务运行正常',
          timestamp: Date.now(),
        };
        this.cachedStatus = status;
        return status;
      } else {
        throw new Error(`HTTP ${response.status}`);
      }

    } catch (error: any) {
      console.error('❌ 后端检查失败:', error);
      
      let message = '后台服务连接失败';
      if (error.name === 'AbortError') {
        message = '后台服务响应超时（可能正在启动中）';
      } else if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
        message = '无法连接到后台服务，请检查服务是否启动';
      } else if (error.message.includes('CORS')) {
        message = '跨域配置异常，后端服务可能需要重启';
      }

      const status: BackendStatus = {
        isOnline: false,
        message,
        timestamp: Date.now(),
      };
      // 失败时不缓存，允许重试
      return status;
    }
  }

  /**
   * 快速检查后台状态（用于登录前检查）
   */
  public async quickCheck(): Promise<boolean> {
    try {
      const status = await this.checkBackendStatus();
      return status.isOnline;
    } catch {
      return false;
    }
  }

  /**
   * 获取后台服务URL
   */
  public getBackendUrl(): string {
    return API_CONFIG.BASE_URL;
  }

  /**
   * 清除缓存状态（用于强制重新检查）
   */
  public clearCache(): void {
    this.cachedStatus = null;
    this.checkPromise = null;
    this.isChecking = false;
  }

  /**
   * 获取缓存状态
   */
  public getCachedStatus(): BackendStatus | null {
    return this.cachedStatus;
  }
}

const backendStatusService = new BackendStatusService();
export default backendStatusService;
